import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { fetchSingleProduct } from '../../services/product-api';
import { useCategoryLookup } from '../../hooks/useCategoryLookup';
import { addItemToRecentlyViewed } from '../../features/product/recentlyViewedSlice';
import { trackProductViewed } from '../../lib/eventTracker';
import { getPromo } from '../getPromo';

export const useProductCard = (product) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.data);
  const {
    productID,
    inStock,
    productName,
    coverImage,
    fairAppPrice,
    slug,
    fairAppSalesPrice,
    minimumDownPaymentPercentage,
    average_rating,
    total_review_count,
  } = product || {};

  const promo = getPromo(product);

  const discountPercent =
    ((fairAppPrice - fairAppSalesPrice) / fairAppPrice) * 100;

  const handleAddToRecentlyViewed = useCallback(() => {
    trackProductViewed(product);
    dispatch(addItemToRecentlyViewed(product));
  }, [dispatch, product]);

  // Try to get category info from product object first (avoids extra API call)
  const productCategory = product?.produtCategory || product?.productCategory;
  const hasCategoryInfo =
    productCategory?.parent_category?.slug && productCategory?.slug;

  // Some endpoints (e.g. search) send produtCategory as a bare subcategory
  // id. Resolve it from the shared, already-cached categories list instead
  // of fetching every product individually (one request per card).
  const { bySubCategoryId, isReady: categoriesLoaded } = useCategoryLookup({
    enabled: !hasCategoryInfo,
  });
  const resolvedCategory = hasCategoryInfo
    ? null
    : bySubCategoryId.get(String(productCategory)) || null;

  // Last resort: fetch the product only if the category still can't be found
  const { data } = useQuery({
    queryKey: ['product-card', productID],
    queryFn: () => fetchSingleProduct(productID),
    enabled:
      !!productID && !hasCategoryInfo && categoriesLoaded && !resolvedCategory,
    staleTime: 1000 * 60 * 10,
  });

  // Use product's category info if available, otherwise the cached categories
  // list, otherwise the fetched product
  const category =
    productCategory?.parent_category?.slug ||
    resolvedCategory?.category ||
    data?.produtCategory?.parent_category?.slug;
  const sub_category =
    productCategory?.slug ||
    resolvedCategory?.sub_category ||
    data?.produtCategory?.slug;

  return {
    user,
    productID,
    inStock,
    productName,
    coverImage,
    fairAppPrice,
    slug,
    fairAppSalesPrice,
    minimumDownPaymentPercentage,
    average_rating,
    total_review_count,
    discountPercent,
    promo,
    category,
    sub_category,
    handleAddToRecentlyViewed,
  };
};
